import React, { useState, useEffect } from 'react';
import { Breadcrumb, Collapse, DatePicker, Select, Switch } from 'antd';
import { HomeOutlined, SaveOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import {
  Form,
  Input,
  Button,
  Space,
  Skeleton,
  ConfigProvider,
  BackTop,
} from 'antd';
import { FieldData } from 'rc-field-form/lib/interface';
import { useHistory } from 'react-router-dom';
import plPL from 'antd/lib/locale/pl_PL';
import TextEditor from 'modules/layoutModule/components/TextEditor/TextEditor';
import 'moment/locale/pl';
import FormCoverUpload from 'modules/galleryModules/components/FormCoverUpload';
import 'dayjs/locale/pl';
import {
  getGtwFiles,
  getGtwRequirements,
  getSpecificGtwEditionRequest,
  saveGtwEditionRequest,
} from '../requests/gtwRequests';
import moment from 'moment';
import dayjs from 'dayjs';

const GtwEditionsEdit = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [fields, setFields] = useState<FieldData[]>([]);
  const [loading, toggleLoading] = useState<boolean>(false);
  const history = useHistory();
  const [isSaveButtonLoading, toggleButtonLoading] = useState(false);
  const [contentValue, setContentValue] = useState<string | undefined>('');
  const [cover, setCover] = useState<string | null>(null);
  const [coverChanged, changeCover] = useState<boolean>(false);
  const [howToUseValue, setHowToUseValue] = useState<string | undefined>('');
  const [userListValue, setUserListValue] = useState<string | undefined>('');
  const [documentsValue, setDocumentsValue] = useState<string | undefined>('');
  const [files, setFiles] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const onContentEdit = (e: string, field: string) => {
    setFields([
      {
        name: [field],
        value: e,
      },
    ]);
  };

  useEffect(() => {
    getGtwFiles().then((response) => {
      if (response) {
        const activeFiles = response.filter(
          (file: { isActive: number }) => file.isActive
        );
        const mapFiles = activeFiles.map(
          (file: { title: string; id: string }) => {
            return {
              label: file.title,
              value: file.id,
            };
          }
        );
        mapFiles.unshift({ label: '', value: '' });
        setFiles(mapFiles);
      }
    });

    getGtwRequirements().then((response) => {
      if (response) {
        const activeRequirements = response.filter(
          (requirement: { isActive: number }) => requirement.isActive
        );
        const mapRequirements = activeRequirements.map(
          (requirement: { title: string; id: string }) => {
            return {
              label: requirement.title,
              value: requirement.id,
            };
          }
        );
        mapRequirements.unshift({ label: '', value: '' });
        setRequirements(mapRequirements);
      }
    });
  }, []);

  useEffect(() => {
    toggleLoading(true);
    getSpecificGtwEditionRequest(id)
      .then((response) => {
        if (response) {
          toggleLoading(false);
          const { data } = response;

          setFields([
            {
              name: ['id'],
              value: data.id,
            },
            {
              name: ['title'],
              value: data.title,
            },
            {
              name: ['fileImage1'],
              value: data.fileImage1,
            },
            {
              name: ['isActive'],
              value: data.isActive,
            },
            {
              name: ['status'],
              value: data.status,
            },
            {
              name: ['idFile1'],
              value: data.idFile1,
            },
            {
              name: ['idFile2'],
              value: data.idFile2,
            },
            {
              name: ['idFile3'],
              value: data.idFile3,
            },
            {
              name: ['idFile4'],
              value: data.idFile4,
            },
            {
              name: ['idFile5'],
              value: data.idFile5,
            },
            {
              name: ['idRequirement1'],
              value: data.idRequirement1,
            },
            {
              name: ['idRequirement2'],
              value: data.idRequirement2,
            },
            {
              name: ['idRequirement3'],
              value: data.idRequirement3,
            },
            {
              name: ['idRequirement4'],
              value: data.idRequirement4,
            },
            {
              name: ['idRequirement5'],
              value: data.idRequirement5,
            },
            {
              name: ['idRequirement6'],
              value: data.idRequirement6,
            },
            {
              name: ['idRequirement7'],
              value: data.idRequirement7,
            },
            {
              name: ['idRequirement8'],
              value: data.idRequirement8,
            },
            {
              name: ['idRequirement9'],
              value: data.idRequirement9,
            },
            {
              name: ['idRequirement10'],
              value: data.idRequirement10,
            },
            {
              name: ['label'],
              value: data.label,
            },
            {
              name: ['name'],
              value: data.name,
            },
            {
              name: ['name2'],
              value: data.name2,
            },
            {
              name: ['edition'],
              value: data.edition,
            },
            {
              name: ['howToUse'],
              value: data.howToUse,
            },
            {
              name: ['documents'],
              value: data.documents,
            },
            {
              name: ['userList'],
              value: data.userList,
            },
            {
              name: ['documents'],
              value: data.documents,
            },
            {
              name: ['startTime'],
              value: data.startTime,
            },
            {
              name: ['dateStart'],
              value: dayjs(data.dateStart, 'YYYY/MM/DD'),
            },
            {
              name: ['dateEnd'],
              value: dayjs(data.dateEnd, 'YYYY/MM/DD'),
            },
          ]);

          setContentValue(data.edition);
          setHowToUseValue(data.howToUse);
          setUserListValue(data.userList);
          setDocumentsValue(data.documents);

          form.setFieldsValue({
            fileImage1: data?.fileImage1,
          });
          setCover(data?.fileImage1);
        } else {
          history.push('/gtw-editions');
        }
      })
      .catch(() => {
        history.push('/gtw-editions');
      });
  }, []);

  const onFinish = (values: any) => {
    toggleButtonLoading(true);
    saveGtwEditionRequest(values).then(() => {
      toggleButtonLoading(false);
    });
  };

  const validateMessages = {
    required: 'Proszę uzupełnić pole ${label}!',
    types: {
      email: '${label} is not a valid email!',
      number: '${label} is not a valid number!',
    },
    number: {
      range: '${label} must be between ${min} and ${max}',
    },
  };

  const updateCover = (value: string | null) => {
    form.setFieldsValue({
      fileImage1: value,
    });
    setCover(value);
    changeCover(true);
  };

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Grunt to Wiedza</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/gtw-editions">Edycje</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to={`/gtw-editions/${id}`}>Edycja wpisu</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <div className="site-layout-background">
          {loading && <Skeleton active />}
          {!loading && (
            <Form
              layout="vertical"
              fields={fields}
              onFinish={onFinish}
              form={form}
              validateMessages={validateMessages}
            >
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name="fileImage1"
                label="Okładka"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <FormCoverUpload
                  cover={cover}
                  setCover={updateCover}
                  isNew={coverChanged}
                  id={form.getFieldsValue()?.id}
                  field="fileImage1"
                  number={1}
                  hideTags
                />
              </Form.Item>

              <Form.Item name="status" label="Status">
                <Select
                  showSearch
                  placeholder="Wybierz status"
                  optionFilterProp="children"
                  options={[
                    { label: null, value: null },
                    { label: 'Wkrótce', value: 'gtw_main/status/wkrotce' },
                    { label: 'Aktualna', value: 'gtw_main/status/aktualny' },
                    {
                      label: 'Zakończona',
                      value: 'gtw_main/status/zakonczona',
                    },
                  ]}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="startTime" label="Start rejestracji">
                <Input />
              </Form.Item>

              <Form.Item
                name="dateStart"
                label="Data rozpoczęcia"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                {/*// @ts-ignore*/}
                <DatePicker />
              </Form.Item>

              <Form.Item
                name="dateEnd"
                label="Data zakończenia"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                {/*// @ts-ignore*/}
                <DatePicker />
              </Form.Item>

              <Form.Item
                name="label"
                label="Nazwa w sliderze"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="name"
                label="Nazwa edycji"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="name2"
                label="Nazwa na podstronie produktu"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="edition"
                label="O edycji programu"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <TextEditor
                  initialValue={contentValue}
                  onChange={(e) => onContentEdit(e, 'edition')}
                  disableGallery
                />
              </Form.Item>

              <Form.Item name="idRequirement1" label="Warunek 1">
                <Select
                  showSearch
                  placeholder="Wybierz wymaganie"
                  optionFilterProp="children"
                  options={requirements}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idRequirement2" label="Warunek 2">
                <Select
                  showSearch
                  placeholder="Wybierz wymaganie"
                  optionFilterProp="children"
                  options={requirements}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idRequirement3" label="Warunek 3">
                <Select
                  showSearch
                  placeholder="Wybierz wymaganie"
                  optionFilterProp="children"
                  options={requirements}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idRequirement4" label="Warunek 4">
                <Select
                  showSearch
                  placeholder="Wybierz wymaganie"
                  optionFilterProp="children"
                  options={requirements}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idRequirement5" label="Warunek 5">
                <Select
                  showSearch
                  placeholder="Wybierz wymaganie"
                  optionFilterProp="children"
                  options={requirements}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idRequirement6" label="Warunek 6">
                <Select
                  showSearch
                  placeholder="Wybierz wymaganie"
                  optionFilterProp="children"
                  options={requirements}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idRequirement7" label="Warunek 7">
                <Select
                  showSearch
                  placeholder="Wybierz wymaganie"
                  optionFilterProp="children"
                  options={requirements}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idRequirement8" label="Warunek 8">
                <Select
                  showSearch
                  placeholder="Wybierz wymaganie"
                  optionFilterProp="children"
                  options={requirements}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idRequirement9" label="Warunek 9">
                <Select
                  showSearch
                  placeholder="Wybierz wymaganie"
                  optionFilterProp="children"
                  options={requirements}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idRequirement10" label="Warunek 10">
                <Select
                  showSearch
                  placeholder="Wybierz wymaganie"
                  optionFilterProp="children"
                  options={requirements}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="howToUse" label="Jak wziąć udział">
                {/* @ts-ignore */}
                <TextEditor
                  initialValue={howToUseValue}
                  onChange={(e) => onContentEdit(e, 'howToUse')}
                  disableGallery
                />
              </Form.Item>

              <Form.Item name="userList" label="Lista uczestników">
                {/* @ts-ignore */}
                <TextEditor
                  initialValue={userListValue}
                  onChange={(e) => onContentEdit(e, 'userList')}
                  disableGallery
                />
              </Form.Item>

              <Form.Item name="documents" label="Dokumenty">
                {/* @ts-ignore */}
                <TextEditor
                  initialValue={documentsValue}
                  onChange={(e) => onContentEdit(e, 'documents')}
                  disableGallery
                />
              </Form.Item>

              <Form.Item name="idFile1" label="Plik do pobrania 1">
                <Select
                  showSearch
                  placeholder="Wybierz plik"
                  optionFilterProp="children"
                  options={files}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idFile2" label="Plik do pobrania 2">
                <Select
                  showSearch
                  placeholder="Wybierz plik"
                  optionFilterProp="children"
                  options={files}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idFile3" label="Plik do pobrania 3">
                <Select
                  showSearch
                  placeholder="Wybierz plik"
                  optionFilterProp="children"
                  options={files}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item name="idFile4" label="Plik do pobrania 4">
                <Select
                  showSearch
                  placeholder="Wybierz plik"
                  optionFilterProp="children"
                  options={files}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>
              <Form.Item name="idFile5" label="Plik do pobrania 5">
                <Select
                  showSearch
                  placeholder="Wybierz plik"
                  optionFilterProp="children"
                  options={files}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>

              <Form.Item
                name="isActive"
                label="Aktywność"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={isSaveButtonLoading}
                  >
                    Zapisz
                  </Button>
                  <Link to="/gtw-editions">
                    <Button>Powrót</Button>
                  </Link>
                </Space>
              </Form.Item>
            </Form>
          )}
          {loading && (
            <Link to="/gtw-editions">
              <Button>Powrót</Button>
            </Link>
          )}
          <BackTop />
        </div>
      </ConfigProvider>
    </>
  );
};

export default GtwEditionsEdit;
